import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';

// Material UI components
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';

// Material UI icons
import {
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';

// Services
import * as userService from '../../services/userService';

const TransactionHistoryReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    part_id: '',
    location_id: '',
    user_id: '',
    transaction_type: ''
  });
  const [users, setUsers] = useState([]);
  const [parts, setParts] = useState([]);
  const [locations, setLocations] = useState([]);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let queryString = '?';
      if (filters.start_date) queryString += `start_date=${filters.start_date}&`;
      if (filters.end_date) queryString += `end_date=${filters.end_date}&`;
      if (filters.part_id) queryString += `part_id=${filters.part_id}&`;
      if (filters.location_id) queryString += `location_id=${filters.location_id}&`;
      if (filters.user_id) queryString += `user_id=${filters.user_id}&`;
      if (filters.transaction_type) queryString += `transaction_type=${filters.transaction_type}&`;

      const response = await axios.get(`/api/reports/history${queryString}`);
      setTransactions(response.data.transactions);
      setSummary(response.data.summary);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transaction history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await userService.getUsers();
        setUsers(data);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };

    const fetchParts = async () => {
      try {
        const response = await axios.get('/api/parts');
        setParts(response.data);
      } catch (err) {
        console.error('Error fetching parts:', err);
      }
    };

    const fetchLocations = async () => {
      try {
        const response = await axios.get('/api/locations');
        setLocations(response.data);
      } catch (err) {
        console.error('Error fetching locations:', err);
      }
    };

    fetchUsers();
    fetchParts();
    fetchLocations();
    fetchTransactions();
  }, [fetchTransactions]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const handleClearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      part_id: '',
      location_id: '',
      user_id: '',
      transaction_type: ''
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleExportCSV = () => {
    try {
      // Get the authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You must be logged in to export data');
        return;
      }

      // Build query string from filters
      let queryString = '';
      if (filters.start_date) queryString += `start_date=${filters.start_date}&`;
      if (filters.end_date) queryString += `end_date=${filters.end_date}&`;
      if (filters.part_id) queryString += `part_id=${filters.part_id}&`;
      if (filters.location_id) queryString += `location_id=${filters.location_id}&`;
      if (filters.user_id) queryString += `user_id=${filters.user_id}&`;
      if (filters.transaction_type) queryString += `transaction_type=${filters.transaction_type}&`;

      // Remove trailing & if present
      if (queryString.endsWith('&')) {
        queryString = queryString.slice(0, -1);
      }

      // Use axios to make a request with the token in the header
      axios({
        url: `/api/reports/export/history?${queryString}`,
        method: 'GET',
        responseType: 'blob', // Important for handling file downloads
        headers: {
          'x-auth-token': token
        }
      }).then((response) => {
        // Create a blob URL for the file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'transaction_history.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }).catch((error) => {
        console.error('Error downloading file:', error);
        alert('Failed to export report');
      });
    } catch (err) {
      console.error('Error exporting report:', err);
      alert('Failed to export report');
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get transaction type chip
  const getTransactionTypeChip = (type) => {
    let color, label;

    switch (type) {
      case 'add':
        color = 'success';
        label = 'Add';
        break;
      case 'remove':
        color = 'error';
        label = 'Remove';
        break;
      case 'adjust':
        color = 'warning';
        label = 'Adjust';
        break;
      case 'transfer':
        color = 'info';
        label = 'Transfer';
        break;
      default:
        color = 'default';
        label = type;
    }

    return (
      <Chip
        label={label}
        color={color}
        size="small"
        sx={{ textTransform: 'capitalize' }}
      />
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Transaction History
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            sx={{ mr: 1 }}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchTransactions}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Filters</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Start Date"
              type="date"
              name="start_date"
              value={filters.start_date}
              onChange={handleFilterChange}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="End Date"
              type="date"
              name="end_date"
              value={filters.end_date}
              onChange={handleFilterChange}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="part-filter-label">Part</InputLabel>
              <Select
                labelId="part-filter-label"
                id="part-filter"
                name="part_id"
                value={filters.part_id}
                onChange={handleFilterChange}
                label="Part"
              >
                <MenuItem value="">All Parts</MenuItem>
                {parts.map(part => (
                  <MenuItem key={part.id} value={part.id}>
                    {part.internal_part_number} - {part.description.substring(0, 30)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="location-filter-label">Location</InputLabel>
              <Select
                labelId="location-filter-label"
                id="location-filter"
                name="location_id"
                value={filters.location_id}
                onChange={handleFilterChange}
                label="Location"
              >
                <MenuItem value="">All Locations</MenuItem>
                {locations.map(location => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.warehouse?.name} - {location.rack}-{location.shelf}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="user-filter-label">User</InputLabel>
              <Select
                labelId="user-filter-label"
                id="user-filter"
                name="user_id"
                value={filters.user_id}
                onChange={handleFilterChange}
                label="User"
              >
                <MenuItem value="">All Users</MenuItem>
                {users.map(user => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="type-filter-label">Transaction Type</InputLabel>
              <Select
                labelId="type-filter-label"
                id="type-filter"
                name="transaction_type"
                value={filters.transaction_type}
                onChange={handleFilterChange}
                label="Transaction Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="add">Add</MenuItem>
                <MenuItem value="remove">Remove</MenuItem>
                <MenuItem value="adjust">Adjust</MenuItem>
                <MenuItem value="transfer">Transfer</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SearchIcon />}
                onClick={fetchTransactions}
                fullWidth
                sx={{ height: '56px' }}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                fullWidth
                sx={{ height: '56px' }}
              >
                Clear Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs for different views */}
      <Box sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="transaction view tabs">
          <Tab label="Transactions" />
          <Tab label="Summary" />
        </Tabs>
      </Box>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Transactions Tab */}
      {activeTab === 0 && !loading && (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Part</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body1" sx={{ py: 3 }}>
                        No transactions found matching the current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map(transaction => (
                    <TableRow key={transaction.id} hover>
                      <TableCell>
                        {formatDate(transaction.created_at)}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Part Details">
                          <Button
                            component={RouterLink}
                            to={`/inventory/${transaction.part.id}`}
                            size="small"
                            sx={{ textAlign: 'left', justifyContent: 'flex-start' }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2" fontWeight="medium">
                                {transaction.part.internal_part_number}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                                {transaction.part.description}
                              </Typography>
                            </Box>
                          </Button>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          {transaction.location.warehouse?.name} - {transaction.location.rack}-{transaction.location.shelf}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {getTransactionTypeChip(transaction.transaction_type)}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={transaction.quantity_change > 0 ? 'success.main' : 'error.main'}
                          fontWeight="medium"
                        >
                          {transaction.quantity_change > 0 ? `+${transaction.quantity_change}` : transaction.quantity_change}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          {transaction.user.username}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {transaction.notes || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Summary Tab */}
      {activeTab === 1 && !loading && summary && (
        <Grid container spacing={3}>
          {/* Transaction Type Summary */}
          <Grid item xs={12} md={6}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardHeader title="By Transaction Type" />
              <Divider />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(summary.by_transaction_type).map(([type, data]) => (
                        <TableRow key={type} hover>
                          <TableCell>{getTransactionTypeChip(type)}</TableCell>
                          <TableCell align="right">{data.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* User Summary */}
          <Grid item xs={12} md={6}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardHeader title="By User" />
              <Divider />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell align="right">Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(summary.by_user).map(([username, data]) => (
                        <TableRow key={username} hover>
                          <TableCell>{username}</TableCell>
                          <TableCell>{data.role}</TableCell>
                          <TableCell align="right">{data.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Part Summary */}
          <Grid item xs={12}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardHeader title="By Part" />
              <Divider />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Part Number</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(summary.by_part).map(([partNumber, data]) => (
                        <TableRow key={partNumber} hover>
                          <TableCell>{partNumber}</TableCell>
                          <TableCell>{data.description}</TableCell>
                          <TableCell align="right">{data.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default TransactionHistoryReport;
